// iter-111G — Tiny helpers for displaying USD currency consistently
// across the app. Two flavours:
//
//   1. formatUSD(num)   — strict numeric → "$1,234,567" (Intl USD).
//                         Use for pure-numeric fields like Budget
//                         amounts where the value is stored as a
//                         Number in the DB.
//
//   2. displayMoney(text) — free-form text that MIGHT already contain
//                          "$" or "%" or "TBD" / "1.2M". Adds a
//                          leading "$ " only when the value looks like
//                          a bare number and isn't already prefixed.
//                          Safe to pass any string.
//
// Why both? Money "values" in this app live in two shapes:
//   • Strictly numeric DB fields (e.g. Budget rows where amount is a
//     Number) → `formatUSD`
//   • Free-form text fields (ROI investment / ROI return / inventory
//     subtitle) where the admin may type "TBD", "1.2M/yr savings",
//     "$80k–$120k". These keep their original wording, we just add a
//     leading "$" when it's missing AND the value looks numeric.

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatUSD(value) {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.\-]/g, ''));
  if (!Number.isFinite(num)) return '';
  return USD.format(num);
}

/**
 * Format a free-form currency string for DISPLAY.
 * Examples:
 *   displayMoney('2500000')           → '$2,500,000'
 *   displayMoney('2.5M')              → '$2.5M'
 *   displayMoney('TBD')               → 'TBD'                  (no digits → leave)
 *   displayMoney('$80k')              → '$80k'                 (already $)
 *   displayMoney('40k/yr · 3-yr pay') → '$40k/yr · 3-yr pay'
 *   displayMoney('18% over 5 yrs')    → '18% over 5 yrs'       ("%" → already a rate, no $)
 *   displayMoney('')                  → ''
 */
export function displayMoney(text) {
  if (text === null || text === undefined) return '';
  const raw = String(text).trim();
  if (!raw) return '';
  // iter-124 — Already-prefixed bare number like "$2500000" or "$ 1,234"
  // — re-format through Intl so it lands as $2,500,000.00.
  const prefixedNum = raw.match(/^([$€£¥₹])\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*$/);
  if (prefixedNum) {
    const sym = prefixedNum[1];
    const num = Number(prefixedNum[2].replace(/,/g, ''));
    if (Number.isFinite(num)) {
      if (sym === '$') return formatUSD(num);
      return `${sym}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }
  // iter-124 — Numeric RANGE patterns, with or without a $ prefix and
  // either ASCII hyphen, en-dash, em-dash or " to ". Examples:
  //   "80000-90000"        → "$80,000.00 – $90,000.00"
  //   "$80,000 – $120,000" → "$80,000.00 – $120,000.00"
  //   "80k-120k"           → falls through (qualitative)
  const rangeMatch = raw.match(
    /^\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*[-–—]|to\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*$/i
  );
  // Simpler & safer two-step: split on the separator first.
  const rangeParts = raw.replace(/\$/g, '').split(/\s*[-–—]\s*|\s+to\s+/i);
  if (rangeParts.length === 2) {
    const a = rangeParts[0].trim();
    const b = rangeParts[1].trim();
    if (/^[0-9][0-9,]*(?:\.[0-9]+)?$/.test(a) && /^[0-9][0-9,]*(?:\.[0-9]+)?$/.test(b)) {
      return `${formatUSD(a)} – ${formatUSD(b)}`;
    }
  }
  void rangeMatch; // keep linter happy; kept for future single-side handling
  // Already has a currency prefix on a free-form string.
  if (/^[$€£¥₹]/.test(raw)) return raw;
  if (/^J\$/i.test(raw)) return raw;
  // Already an explicit non-monetary expression (pure percentage,
  // ratio, or qualitative label). Don't add a dollar sign — it would
  // be misleading on "18% IRR" or "TBD".
  if (/^[^0-9]*$/.test(raw)) return raw;          // no digit at all
  if (/^[0-9]+\s*%/.test(raw)) return raw;        // starts with "X%"
  // Pure ratio "3:1" or "1-yr"-style strings without any $ — leave.
  if (/^\s*[0-9.]+\s*(:|yrs?\b|years?\b|months?\b|weeks?\b)/i.test(raw)) return raw;
  // iter-124 — If the string is a BARE number, run it through formatUSD.
  if (/^[0-9][0-9,]*(?:\.[0-9]+)?\s*$/.test(raw)) {
    return formatUSD(raw);
  }
  // Otherwise prepend "$ ".
  return `$ ${raw}`;
}
